import { useAuth0 } from '@auth0/auth0-react'
import { t, Trans } from '@lingui/macro'
import { Auth0OrganizationDTO } from '@om1/aspen-api/codegen/client'
import { useUserOrgList } from '@om1/aspen-api/codegen/hooks/useUserOrgList'
import { jwtDecode } from 'jwt-decode'
import { AlertCircle, ChevronDown, LogOut, Menu, SquareChevronUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import aspenLogo from '../assets/logo_Aspen.png'
import { aspenAuth0Config } from '../config/aspen-auth0-config'
import { routeConfig } from '../pages/LandingPage'
import { AspenRoutes } from '../routes'

interface Organization extends Auth0OrganizationDTO {
    id: string
    name: string
    display_name: string
}

const Navbar = () => {
    const { isAuthenticated, user, logout, getAccessTokenSilently, loginWithRedirect } = useAuth0()
    const { organizations, loading: orgLoading, error: orgError, fetchOrganizations } = useUserOrgList()
    const [permissions, setPermissions] = useState<string[]>([])
    const [isOrgMenuExpanded, setIsOrgMenuExpanded] = useState(false)
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (isAuthenticated && user?.org_name && organizations) {
            const currentOrg = organizations.organizations.find((org) => org.name === user.org_name)
            setSelectedOrg(currentOrg || null)
        }
    }, [isAuthenticated, user?.org_name, organizations])

    useEffect(() => {
        getAccessTokenSilently().then((token) => {
            const decodedToken = jwtDecode<{ [key: string]: any }>(String(token))
            setPermissions(decodedToken.permissions || [])
        })
    }, [getAccessTokenSilently])

    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false)
    const [isUserMenuExpanded, setIsUserMenuExpanded] = useState(false)

    const handleOrgSelect = async (org: Organization) => {
        try {
            setIsLoading(true)
            setIsOrgMenuExpanded(false)

            // First try silent authentication
            try {
                const token = await getAccessTokenSilently({
                    cacheMode: 'off',
                    authorizationParams: {
                        organization: org.id,
                        scope: t`openid profile email`,
                        audience: aspenAuth0Config.auth0AudienceDomain
                        // Remove prompt: "consent" as it forces consent screen
                    }
                })

                const decodedToken = jwtDecode<{ permissions: string[] }>(String(token))
                setPermissions(decodedToken.permissions || [])
                setSelectedOrg(org)
                window.location.reload() // Refresh to ensure new permissions are applied
            } catch (silentError) {
                console.log(silentError)

                // If silent fails, do full redirect with logout first
                await logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                        localOnly: true // Only clear local session
                    }
                })

                // Then redirect to login with new organization
                await loginWithRedirect({
                    authorizationParams: {
                        organization: org.id,
                        prompt: 'consent', // Force login screen but don't force consent
                        redirect_uri: window.location.origin,
                        audience: aspenAuth0Config.auth0AudienceDomain
                    },
                    appState: {
                        returnTo: window.location.pathname,
                        targetOrg: org.id
                    }
                })
            }
        } catch (error) {
            console.error(t`Failed to switch organization:`, error)
        } finally {
            setIsLoading(false)
        }
    }

    const getLinkClass = (isActive: boolean) =>
        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
        `flex items-center ${isSidebarExpanded ? 'gap-3 -ml-0.5 w-full' : 'justify-center w-12'} px-4 py-3 rounded-lg transition-all w-full ${
            isActive
                ? // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                  'bg-primary-dark text-primary-main'
                : // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                  'text-primary-main hover:bg-primary-dark/90'
        }`

    return (
        <div className='z-[9999] relative'>
            {/* Top Bar */}
            <header className='fixed top-0 left-0 right-0 h-16 bg-primary-main shadow-lg z-[9999]'>
                <div className='h-full pl-3 pr-6 flex items-center gap-3'>
                    <button
                        onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                        className='p-2 rounded-lg text-primary-contrast hover:bg-primary-light/20 transition-colors'
                        aria-label={t`Toggle sidebar`}
                    >
                        <Menu size={24} />
                    </button>
                    <img src={aspenLogo} alt={t`Aspen Logo`} className='h-12 w-auto' />
                    <h1 className='text-2xl font-bold text-gray-light shrink-0'>
                        <Trans>OM1 Central Site</Trans>
                    </h1>
                    {isAuthenticated && (
                        <div className='text-gray-light flex items-center justify-end gap-4 w-full'>
                            {/* Organization Dropdown */}
                            <div className='relative'>
                                <button
                                    className='flex items-center gap-2 px-3 py-2 rounded-md hover:bg-primary-light/20 transition-colors'
                                    onClick={() => setIsOrgMenuExpanded(!isOrgMenuExpanded)}
                                    aria-label={t`Select organization`}
                                    disabled={isLoading || orgLoading}
                                >
                                    <span className='text-gray-light'>
                                        {selectedOrg?.display_name || user?.org_name?.toUpperCase()}
                                        {(isLoading || orgLoading) && <span className='ml-2 inline-block animate-spin'>⌛</span>}
                                        {orgError && (
                                            <span className='ml-2 text-red-500' title={orgError}>
                                                <AlertCircle size={16} />
                                            </span>
                                        )}
                                    </span>
                                    <ChevronDown size={16} className={`transition-transform duration-200 ${isOrgMenuExpanded ? 'rotate-180' : ''}`} />
                                </button>
                                {isOrgMenuExpanded && (
                                    <div className='fixed top-[4rem] mt-2 w-64 bg-white shadow-2xl rounded-md py-1 z-[10000] border border-gray-100/20 backdrop-blur-sm'>
                                        {orgError ? (
                                            <div className='px-4 py-3 text-red-500 flex items-center gap-2'>
                                                <AlertCircle size={16} />
                                                <span>{orgError}</span>
                                                <button
                                                    onClick={fetchOrganizations}
                                                    className='ml-auto text-primary-main hover:text-primary-dark'
                                                    aria-label={t`Retry loading organizations`}
                                                >
                                                    <Trans>Retry</Trans>
                                                </button>
                                            </div>
                                        ) : (
                                            organizations?.organizations.map((org) => (
                                                <button
                                                    key={org.id}
                                                    className={`w-full text-left px-4 py-2 hover:bg-primary-light/20 transition-colors ${
                                                        selectedOrg?.id === org.id ? 'bg-primary-light/10 text-primary-main' : 'text-gray-700'
                                                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    onClick={() => handleOrgSelect(org)}
                                                    disabled={isLoading}
                                                    aria-label={t`Switch to ${org.display_name}`}
                                                >
                                                    {org.display_name}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* User Menu */}
                            <div className='flex flex-col items-end'>
                                <h2 className='text-md'>
                                    {user?.given_name} {user?.family_name}
                                </h2>
                            </div>
                            <button
                                className='text-white flex items-center gap-2'
                                onClick={() => setIsUserMenuExpanded(!isUserMenuExpanded)}
                                aria-label={t`Toggle user menu`}
                            >
                                <SquareChevronUp size={12} className={`transition-all duration-300 ${isUserMenuExpanded ? 'rotate-180' : ''}`} />
                            </button>
                            <div
                                className={`fixed top-[4rem] right-0 mt-2 w-48 bg-white shadow-lg rounded-md py-2 z-[10000] ${
                                    isUserMenuExpanded ? 'block' : 'hidden'
                                } transition-all duration-300 delay-100`}
                            >
                                <p
                                    className='text-primary-main hover:bg-primary-light/20 px-4 py-2 rounded-md flex items-center gap-2 justify-center'
                                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                                >
                                    <Trans>Logout</Trans>
                                    <LogOut size={12} />
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Sidebar */}
            <nav
                className={`fixed top-16 left-0 bottom-0 bg-primary-light shadow-lg transition-all duration-300 overflow-hidden z-[9998] ${
                    isSidebarExpanded ? 'w-64' : 'w-16'
                }`}
            >
                <div style={{ zIndex: 1000 }} className={`flex flex-col gap-2 p-2 h-full`}>
                    {routeConfig
                        .filter(
                            (route) =>
                                route.route !== AspenRoutes.NOTFOUND &&
                                route.route !== AspenRoutes.ACCESS_DENIED &&
                                route.permissions.every((permission) => permissions?.includes(permission)) &&
                                route.name !== undefined &&
                                route.icon !== undefined
                        )
                        .map((route) => (
                            <NavLink
                                key={route.route}
                                to={`/${route.route}`}
                                isActive={() => {
                                    return window.location.pathname.includes(route.route)
                                }}
                                // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                                activeClassName={'bg-primary-dark text-primary-main'}
                                className={`${getLinkClass(false)} ${isSidebarExpanded ? 'w-full' : 'w-12'}`}
                                onClick={() => setIsSidebarExpanded(false)}
                            >
                                {route.icon}
                                <span className={`transition-all duration-300 ${isSidebarExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
                                    {route.name}
                                </span>
                            </NavLink>
                        ))}
                </div>
            </nav>
        </div>
    )
}

export default Navbar
